/* eslint-disable @typescript-eslint/no-explicit-any */
import { dehydrate, QueryClient } from '@tanstack/react-query'
import type {
  UserCalculateFiatDepositCostInput,
  UserCalculateWithdrawalCostInput,
  UserGetAvailableCryptoContractsParams,
  UserGetFiatTransactionHistoryInput,
  UserPaymentVerifyFiatDepositInput,
} from '../api'
import userPaymentService from './user-payment.service'
import { EUserPaymentQueryKey } from './user-payment.types'

export const paymentVerifyDepositFiatOptions = (
  filter: UserPaymentVerifyFiatDepositInput,
) => {
  return {
    queryKey: [EUserPaymentQueryKey.PAYMENT_VERIFY_DEPOSIT_FIAT, filter],
    queryFn: () => userPaymentService.paymentVerifyDepositFiat(filter),
  }
}

export const userGetAvailableCryptoContractsOptions = (
  filter: UserGetAvailableCryptoContractsParams,
) => {
  return {
    queryKey: [EUserPaymentQueryKey.GET_AVAILABLE_CRYPTO_CONTRACTS, filter],
    queryFn: () => userPaymentService.userGetAvailableCryptoContracts(filter),
  }
}

export const calculateDepositCostOptions = (
  filter: UserCalculateFiatDepositCostInput,
) => {
  return {
    queryKey: [EUserPaymentQueryKey.CALCULATE_DEPOSIT_COST, filter],
    queryFn: () => userPaymentService.calculateDepositCost(filter),
    enabled: !!filter.amount && +filter.amount >= 20_000,
  }
}

export const calculateWithdrawalCostOptions = (
  filter: UserCalculateWithdrawalCostInput,
) => {
  return {
    queryKey: [EUserPaymentQueryKey.CALCULATE_WITHDRAWAL_COST, filter],
    queryFn: () => userPaymentService.calculateWithdrawalCost(filter),
    enabled: !!filter.amount && +filter.amount >= 20_000,
  }
}

export const prefetchPaymentVerifyDepositFiat = async (
  filter: UserPaymentVerifyFiatDepositInput,
) => {
  const queryClient = new QueryClient()
  await queryClient.prefetchQuery(paymentVerifyDepositFiatOptions(filter))
  return dehydrate(queryClient)
}

export const prefetchCalculateDepositCost = async (
  filter: UserCalculateFiatDepositCostInput,
) => {
  const queryClient = new QueryClient()
  await queryClient.prefetchQuery(calculateDepositCostOptions(filter))
  return dehydrate(queryClient)
}

export const getFiatTransactionHistoryOptions = (
  params: UserGetFiatTransactionHistoryInput,
) => {
  return {
    queryKey: [EUserPaymentQueryKey.GET_FIAT_TRANSACTION_HISTORY, params],

    queryFn: ({ pageParam = 0 }) =>
      userPaymentService.getFiatTransactionHistory({
        ...params,
        skip: pageParam * (params.limit || 10),
      }),
    staleTime: 1,
    getNextPageParam: (lastPage: any, allPages: any) => {
      const total = lastPage.data?.total || 0
      const messages = lastPage.data?.transactions || []
      const limit = params.limit || 10

      // If we have total count, use it for pagination
      if (total !== undefined && total !== null) {
        const currentLength = allPages.reduce(
          (acc: number, page: any) =>
            acc + (page.data?.transactions?.length || 0),
          0,
        )
        return currentLength < total ? allPages.length : undefined
      }

      // Fallback: If no total, check if we got fewer messages than requested
      // This indicates we've reached the end
      if (messages.length < limit) {
        return undefined
      }

      // If we got exactly the limit, there might be more data
      return allPages.length
    },
    initialPageParam: 0,
  }
}
